import { CATEGORY_TAG, createTagsForList, QUESTION_TAG, RUBRIC_TAG, SUBMISSION_TAG } from './utils/tags'
import {
    normalizeFetchQuestionChangesHistoryResponse,
    normalizeFetchQuestionMetadataResponse,
    normalizeFetchQuestionsResponse,
} from './utils/normalizers/question.normalizers'
import {
    onFetchQuestionProgressesQueryStarted,
    onUpdateQuestionQueryStarted,
} from './utils/response-handlers/question.api.response-handlers'
import baseApi from './base.api'
import { createSearchParams } from '../../utils/request.helper'
import { onUpdatePatternQueryStarted } from './utils/response-handlers/pattern.api.response-handlers'
import { flatten } from '../../utils/array.helper'

export const questionApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchQuestions: builder.query({
            query: ({ token, versionId }) => ({
                url: `/questions/${token}/detailed`,
                method: 'GET',
                params: { version_id: versionId },
            }),
            transformResponse: normalizeFetchQuestionsResponse,
            providesTags: result => {
                const categories = flatten(result || []).flatMap(q => q?.categories || [])
                return [...createTagsForList(result, QUESTION_TAG), ...createTagsForList(categories, CATEGORY_TAG)]
            },
        }),
        updateQuestion: builder.mutation({
            query: ({
                id,
                name,
                text,
                sectionId,
                wordsLimit,
                type,
                dependsOnQuestionIds,
                questionType,
                note,
            }) => ({
                url: `/questions/${id}`,
                method: 'PATCH',
                body: {
                    name,
                    text,
                    section: sectionId,
                    word_limit: wordsLimit,
                    type,
                    depends_on: dependsOnQuestionIds,
                    question_type: questionType,
                    note,
                },
            }),
            onQueryStarted: onUpdateQuestionQueryStarted,
            invalidatesTags: (result, error, { id }) => [{ type: QUESTION_TAG, id }],
        }),
        createQuestion: builder.mutation({
            query: ({ token, question }) => ({
                url: `/questions/${token}`,
                method: 'POST',
                body: {
                    name: question.name,
                    text: question.text,
                    word_limit: question.wordsLimit,
                    depends_on: question.dependsOnQuestionIds,
                    question_type: question.questionType,
                    note: question.note,
                },
            }),
            invalidatesTags: [QUESTION_TAG],
        }),
        batchUpdateQuestions: builder.mutation({
            query: ({ token, questions }) => ({
                url: `/text-models/${token}/update-questions-recursive`,
                method: 'POST',
                body: questions?.map(
                    ({ id, name, wordsLimit, categories }) => ({
                        id,
                        name,
                        word_limit: wordsLimit,
                        categories: categories?.map(({ id, name, feedback, grade }) => ({ id, name, feedback, grade })),
                    }),
                ),
            }),
            invalidatesTags: [QUESTION_TAG],
        }),
        fetchQuestionMetadata: builder.query({
            query: ({ id }) => ({
                url: `/questions/${id}/metadata`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchQuestionMetadataResponse,
        }),
        updateCategory: builder.mutation({
            query: ({ id, colorId, name, description, type, feedback, grade, attributes, conceptualLabels, philosophyText, autoApprove }) => ({
                url: `/ftm-patterns/${id}`,
                method: 'PUT',
                body: {
                    type,
                    name,
                    color_id: colorId,
                    description,
                    feedback,
                    grade,
                    attributes,
                    conceptual_labels: conceptualLabels,
                    philosophy_text: philosophyText,
                    ...(autoApprove !== undefined && { auto_approve: autoApprove }),
                },
            }),
            onQueryStarted: onUpdatePatternQueryStarted,
            invalidatesTags: (result, error, { id, questionId }) => [
                { type: CATEGORY_TAG, id },
                { type: CATEGORY_TAG },
                { type: RUBRIC_TAG, id: 'LIST' },
                ...(questionId ? [{ type: QUESTION_TAG, id: questionId }] : []),
            ],
        }),
        /**
         * @deprecated Use fetchRubricProgress from rubric.api.js instead.
         * This returns progress from the first associated QuestionRubric.
         */
        fetchQuestionProgresses: builder.query({
            query: ({ ids }) => ({
                url: `/questions/processing-progress`,
                method: 'GET',
                params: createSearchParams({ question_id: ids }),
            }),
            onQueryStarted: onFetchQuestionProgressesQueryStarted,
        }),
        /**
         * Create a category/pattern. 
         * @param rubricId - The rubric to add the pattern to (new architecture)
         * @param questionId - @deprecated Use rubricId instead
         */
        createCategory: builder.mutation({
            query: ({ questionId, rubricId, name, feedback, description, grade, colorId, conceptualLabels, philosophyText, autoApprove }) => ({
                url: '/ftm-patterns/',
                method: 'POST',
                body: {
                    // Support both old (questionId) and new (rubricId) patterns
                    ...(rubricId ? { rubric_id: rubricId } : { question_id: questionId }),
                    name,
                    feedback,
                    description,
                    grade,
                    color_id: colorId,
                    conceptual_labels: conceptualLabels,
                    philosophy_text: philosophyText,
                    auto_approve: autoApprove !== undefined ? autoApprove : true,
                },
            }),
            invalidatesTags: (result, error, { questionId }) => [
                { type: CATEGORY_TAG },
                ...(questionId ? [{ type: QUESTION_TAG, id: questionId }] : []),
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),
        reorderCategories: builder.mutation({
            query: ({ questionId, idToOrderMap }) => ({
                url: `/ftm-patterns/${questionId}/reorder`,
                method: 'PATCH',
                body: idToOrderMap,
            }),
            invalidatesTags: (result, error, { questionId }) => [
                { type: CATEGORY_TAG },
                { type: QUESTION_TAG, id: questionId },
            ],
        }),
        fetchQuestionChangesHistory: builder.query({
            query: ({ id, versionId }) => ({
                url: `/questions/${id}/changes-history`,
                method: 'GET',
                params: { versionId },
            }),
            transformResponse: normalizeFetchQuestionChangesHistoryResponse,
            providesTags: (result, error, { id }) => [
                { type: CATEGORY_TAG },
                { type: QUESTION_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id },
            ],
        }),
        deleteCategory: builder.mutation({
            query: ({ categoryId }) => ({
                url: `/ftm-patterns/${categoryId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { categoryId }) => [
                { type: CATEGORY_TAG, id: categoryId },
                { type: SUBMISSION_TAG },
                { type: QUESTION_TAG, id: 'LIST' },
                { type: RUBRIC_TAG, id: 'LIST' },
            ],
        }),
    }),
})

export const {
    useFetchQuestionsQuery,
    useUpdateQuestionMutation,
    useFetchQuestionMetadataQuery,
    useUpdateCategoryMutation,
    useLazyFetchQuestionProgressesQuery,
    useCreateCategoryMutation,
    useReorderCategoriesMutation,
    useBatchUpdateQuestionsMutation,
    useFetchQuestionChangesHistoryQuery,
    useDeleteCategoryMutation,
    useCreateQuestionMutation,
} = questionApi
